import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import i18n from "i18next";
import { api, getAdminBackupToken, setAdminBackupToken, setAuthToken } from "./api";
import type { Locale } from "../../shared/locales";
import { isLocale } from "../../shared/locales";

export interface User {
  id: number;
  email: string;
  name: string;
  lang: Locale;
  role: "user" | "admin";
  email_verified: boolean;
  is_migrated: boolean;
  password_changed_at: string | null;
  secondary_email: string | null;
  secondary_email_verified: boolean;
  secondary_email_pending: string | null;
  totp_enabled: boolean;
  /** Set when the current session is an admin impersonating this user. */
  impersonator_id: number | null;
}

export type LoginResult =
  | { kind: "ok" }
  | { kind: "totp_required"; totp_session: string };

export type PlanTier = "free" | "starter" | "pro";

export interface PlanState {
  plan: PlanTier;
  status: string | null;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  plan: PlanState | null;
  signup: (data: { email: string; password: string; name: string; lang?: Locale; website?: string }) => Promise<void>;
  login: (data: { email: string; password: string }) => Promise<LoginResult>;
  loginWithTotp: (data: { totp_session: string; code: string }) => Promise<void>;
  loginWithPasskey: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshPlan: () => Promise<void>;
  startImpersonation: (token: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
  isImpersonating: boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<PlanState | null>(null);

  async function refreshPlan() {
    try {
      const data = await api<{ plan: PlanTier; status: string | null }>("/api/audits/usage");
      setPlan({ plan: data.plan, status: data.status });
    } catch {
      setPlan(null);
    }
  }

  async function applyUserLang(u: User) {
    if (isLocale(u.lang) && i18n.language !== u.lang) {
      await i18n.changeLanguage(u.lang);
    }
  }

  async function refresh() {
    try {
      const data = await api<{ user: User }>("/api/auth/me");
      setUser(data.user);
      await applyUserLang(data.user);
      void refreshPlan();
    } catch {
      setAuthToken(null);
      setUser(null);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function signup(data: { email: string; password: string; name: string; lang?: Locale; website?: string }) {
    const res = await api<{ user: User; token: string }>("/api/auth/signup", { method: "POST", json: data });
    setAuthToken(res.token);
    setUser(res.user);
    await applyUserLang(res.user);
    void refreshPlan();
  }

  async function login(data: { email: string; password: string }): Promise<LoginResult> {
    const res = await api<
      | { user: User; token: string }
      | { requires_totp: true; totp_session: string }
    >("/api/auth/login", { method: "POST", json: data });
    if ("requires_totp" in res) {
      return { kind: "totp_required", totp_session: res.totp_session };
    }
    setAuthToken(res.token);
    setUser(res.user);
    await applyUserLang(res.user);
    void refreshPlan();
    return { kind: "ok" };
  }

  async function loginWithTotp(data: { totp_session: string; code: string }) {
    const res = await api<{ user: User; token: string }>("/api/auth/totp/login", { method: "POST", json: data });
    setAuthToken(res.token);
    setUser(res.user);
    await applyUserLang(res.user);
    await refresh();
  }

  async function loginWithPasskey() {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const options = await api<{ challenge: string; [k: string]: unknown }>("/api/auth/passkeys/auth-options");
    const response = await startAuthentication({ optionsJSON: options as Parameters<typeof startAuthentication>[0]["optionsJSON"] });
    const res = await api<{ user: User; token: string }>("/api/auth/passkeys/auth-verify", {
      method: "POST",
      json: { challenge: options.challenge, response },
    });
    setAuthToken(res.token);
    setUser(res.user);
    await applyUserLang(res.user);
    await refresh();
  }

  async function logout() {
    try { await api("/api/auth/logout", { method: "POST" }); } catch { /* fine */ }
    setAuthToken(null);
    setAdminBackupToken(null);
    setUser(null);
    setPlan(null);
  }

  async function startImpersonation(impersonationToken: string) {
    // Save the admin's own token so exitImpersonation can put it back.
    const adminToken = localStorage.getItem("ms_auth_token");
    if (adminToken) setAdminBackupToken(adminToken);
    setAuthToken(impersonationToken);
    await refresh();
  }

  async function exitImpersonation() {
    const adminToken = getAdminBackupToken();
    setAdminBackupToken(null);
    setAuthToken(adminToken);
    await refresh();
  }

  const isImpersonating = user?.impersonator_id != null;

  return (
    <Ctx.Provider value={{ user, loading, plan, signup, login, loginWithTotp, loginWithPasskey, logout, refresh, refreshPlan, startImpersonation, exitImpersonation, isImpersonating }}>
      {children}
    </Ctx.Provider>
  );
}

/** Effective plan including admin override. Returns null until loaded. */
export function usePlan(): PlanTier | null {
  const { user, plan } = useAuth();
  if (user?.role === "admin") return "pro";
  return plan?.plan ?? null;
}

export function isPaidPlan(plan: PlanTier | null): boolean {
  return plan === "starter" || plan === "pro";
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}
